import { ChevronLeft, ChevronRight } from 'lucide-react';
import { forwardRef, HTMLAttributes, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { Button } from './button';
import { Select, type SelectOption } from './select';

type WeekStartDay = 0 | 1; // 0 = Sunday, 1 = Monday

interface DateRange {
  readonly from: Date | undefined;
  readonly to: Date | undefined;
}

interface BaseCalendarProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onSelect'
> {
  readonly disabled?: boolean;
  readonly weekStartsOn?: WeekStartDay;
  readonly showWeekStartToggle?: boolean;
  readonly onWeekStartChange?: (weekStart: WeekStartDay) => void;
}

interface SingleCalendarProps extends BaseCalendarProps {
  readonly mode?: 'single';
  readonly selected?: Date;
  readonly onSelect?: (date: Date | undefined) => void;
}

interface RangeCalendarProps extends BaseCalendarProps {
  readonly mode: 'range';
  readonly selected?: DateRange | undefined;
  readonly onSelect?: (range: DateRange | undefined) => void;
}

export type CalendarProps = SingleCalendarProps | RangeCalendarProps;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Helper functions for date comparisons
 */
const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const isWithinRange = (date: Date, range: DateRange): boolean => {
  if (!range.from || !range.to) return false;
  const time = date.getTime();
  return time >= range.from.getTime() && time <= range.to.getTime();
};

const isRangeStart = (date: Date, range: DateRange): boolean => {
  return range.from ? isSameDay(date, range.from) : false;
};

const isRangeEnd = (date: Date, range: DateRange): boolean => {
  return range.to ? isSameDay(date, range.to) : false;
};

const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      className,
      selected,
      onSelect,
      disabled = false,
      mode = 'single',
      weekStartsOn = 0,
      showWeekStartToggle = false,
      onWeekStartChange,
      ...props
    },
    ref
  ) => {
    const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
    const [internalWeekStart, setInternalWeekStart] =
      useState<WeekStartDay>(weekStartsOn);

    // Track the currently viewed month independently of selected date
    const [viewedMonth, setViewedMonth] = useState(() => {
      if (mode === 'range') {
        const rangeSelected = selected as DateRange | undefined;
        return rangeSelected?.from ?? new Date();
      }
      return (selected as Date | undefined) ?? new Date();
    });

    const year = viewedMonth.getFullYear();
    const month = viewedMonth.getMonth();

    // Use controlled or internal week start
    const effectiveWeekStart = showWeekStartToggle
      ? internalWeekStart
      : weekStartsOn;

    // Get first day of month and total days
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    // Calculate offset based on week start preference
    const offset = (firstDayOfMonth - effectiveWeekStart + 7) % 7;

    // Generate calendar days array with dates from prev/current/next months
    type CalendarDay = {
      day: number;
      date: Date;
      isCurrentMonth: boolean;
    };
    const calendarDays: CalendarDay[] = [];

    // Add days from previous month
    for (let i = offset - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      calendarDays.push({
        day,
        date: new Date(year, month - 1, day),
        isCurrentMonth: false,
      });
    }

    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push({
        day,
        date: new Date(year, month, day),
        isCurrentMonth: true,
      });
    }

    // Add days from next month to complete the grid (6 rows × 7 days = 42 days)
    const remainingDays = 42 - calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      calendarDays.push({
        day,
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
      });
    }

    const handleWeekStartChange = (newWeekStart: WeekStartDay) => {
      setInternalWeekStart(newWeekStart);
      onWeekStartChange?.(newWeekStart);
    };

    const handleRangeSelection = (
      clickedDate: Date,
      rangeSelected: DateRange | undefined,
      rangeSelect: (range: DateRange | undefined) => void
    ) => {
      // Range selection logic: start -> end -> reset
      if (!rangeSelected?.from || (rangeSelected.from && rangeSelected.to)) {
        // Start new range
        rangeSelect({ from: clickedDate, to: undefined });
      } else if (rangeSelected.from && !rangeSelected.to) {
        // Complete the range
        if (clickedDate < rangeSelected.from) {
          // Swap if end is before start
          rangeSelect({ from: clickedDate, to: rangeSelected.from });
        } else {
          rangeSelect({ from: rangeSelected.from, to: clickedDate });
        }
      }
    };

    const handlePrevMonth = () => {
      if (!disabled) {
        setViewedMonth(new Date(year, month - 1, 1));
      }
    };

    const handleNextMonth = () => {
      if (!disabled) {
        setViewedMonth(new Date(year, month + 1, 1));
      }
    };

    const handleDayClick = (clickedDate: Date) => {
      if (!disabled && onSelect) {
        if (mode === 'range') {
          const rangeSelected = selected as DateRange | undefined;
          const rangeSelect = onSelect as (
            range: DateRange | undefined
          ) => void;
          handleRangeSelection(clickedDate, rangeSelected, rangeSelect);
        } else {
          const singleSelect = onSelect as (date: Date | undefined) => void;
          singleSelect(clickedDate);
        }
      }
    };

    const handleMonthChange = (newMonth: number) => {
      if (!disabled) {
        setViewedMonth(new Date(year, newMonth, 1));
        setShowMonthYearPicker(false);
      }
    };

    const handleYearChange = (newYear: number) => {
      if (!disabled) {
        setViewedMonth(new Date(newYear, month, 1));
        setShowMonthYearPicker(false);
      }
    };

    const isSelectedDay = (date: Date): boolean => {
      if (!selected) return false;

      if (mode === 'range') {
        const rangeSelected = selected as DateRange;
        return (
          ((rangeSelected.from && isSameDay(date, rangeSelected.from)) ||
            (rangeSelected.to && isSameDay(date, rangeSelected.to))) ??
          false
        );
      }

      const singleSelected = selected as Date;
      return isSameDay(date, singleSelected);
    };

    const isInRange = (date: Date): boolean => {
      if (mode !== 'range' || !selected) return false;
      const rangeSelected = selected as DateRange;
      return isWithinRange(date, rangeSelected);
    };

    const isRangeStartDay = (date: Date): boolean => {
      if (mode !== 'range' || !selected) return false;
      const rangeSelected = selected as DateRange;
      return isRangeStart(date, rangeSelected);
    };

    const isRangeEndDay = (date: Date): boolean => {
      if (mode !== 'range' || !selected) return false;
      const rangeSelected = selected as DateRange;
      return isRangeEnd(date, rangeSelected);
    };

    const getDayClasses = (
      selected: boolean,
      inRange: boolean,
      rangeStart: boolean,
      rangeEnd: boolean
    ): string => {
      const isSingleDayRange = rangeStart && rangeEnd && selected;

      return cn(
        'flex h-8 w-full items-center justify-center text-sm transition-colors',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        // Range styling
        inRange && !selected && 'bg-accent/50',
        // Single day range (from === to) - fully rounded
        isSingleDayRange && 'rounded-md',
        // Range start (not single day)
        rangeStart && !isSingleDayRange && 'rounded-l-md rounded-r-none',
        // Range end (not single day)
        rangeEnd && !isSingleDayRange && 'rounded-r-md rounded-l-none',
        // Days in middle of range or not in range
        !inRange && !selected && 'rounded-md',
        // Selected day styling (endpoints in range mode, or single selection)
        selected &&
          'bg-primary text-primary-foreground hover:bg-primary/90 font-semibold',
        // Single selection mode (not range)
        selected && mode === 'single' && 'rounded-md',
        // Default text color
        !selected && 'text-foreground'
      );
    };

    const monthName = new Date(year, month).toLocaleString('default', {
      month: 'long',
    });

    const MONTHS = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ] as const;

    // Generate year range (current year ± 10 years)
    const yearRange = Array.from(
      { length: 21 },
      (_, i) => new Date().getFullYear() - 10 + i
    );

    // Convert year range to Select options
    const yearOptions: readonly SelectOption[] = yearRange.map((y) => ({
      value: y.toString(),
      label: y.toString(),
    }));

    // Week start options for Select
    const weekStartOptions: readonly SelectOption[] = [
      { value: '0', label: 'Sunday' },
      { value: '1', label: 'Monday' },
    ];

    // Reorder weekdays based on start preference
    const orderedWeekdays = [
      ...WEEKDAYS.slice(effectiveWeekStart),
      ...WEEKDAYS.slice(0, effectiveWeekStart),
    ];

    return (
      <div
        ref={ref}
        className={cn(
          'w-full max-w-sm rounded-lg border border-border bg-card p-4',
          className
        )}
        role="application"
        aria-label="Calendar"
        {...props}
      >
        {/* Header with month/year and navigation */}
        <div className="mb-4 flex items-center justify-between">
          <Button
            type="button"
            variant={ButtonVariant.Outline}
            size={ButtonSize.Icon}
            onClick={handlePrevMonth}
            disabled={disabled}
            aria-label="Previous month"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.Ghost}
            className="text-sm font-semibold"
            onClick={() => setShowMonthYearPicker(!showMonthYearPicker)}
            disabled={disabled}
            aria-label="Select month and year"
          >
            {monthName} {year}
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.Outline}
            size={ButtonSize.Icon}
            onClick={handleNextMonth}
            disabled={disabled}
            aria-label="Next month"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
        </div>

        {/* Week Start Toggle */}
        {showWeekStartToggle && (
          <div className="mb-4">
            <label
              htmlFor="week-start-select"
              className="mb-2 block text-xs font-medium text-foreground"
            >
              Week starts on
            </label>
            <Select
              id="week-start-select"
              options={weekStartOptions}
              value={effectiveWeekStart.toString()}
              onChange={(value) =>
                value && handleWeekStartChange(Number(value) as WeekStartDay)
              }
              disabled={disabled}
              aria-label="Select week start day"
            />
          </div>
        )}

        {/* Month/Year Picker */}
        {showMonthYearPicker && (
          <div className="relative z-10 mb-4 space-y-4 rounded-lg border border-border bg-muted p-4">
            {/* Month Selection */}
            <div>
              <label
                htmlFor="month-select"
                className="mb-2 block text-xs font-medium text-foreground"
              >
                Month
              </label>
              <div className="grid grid-cols-3 gap-2">
                {MONTHS.map((monthName, index) => (
                  <Button
                    key={monthName}
                    type="button"
                    variant={
                      month === index
                        ? ButtonVariant.Default
                        : ButtonVariant.Outline
                    }
                    size={ButtonSize.Small}
                    onClick={() => handleMonthChange(index)}
                    disabled={disabled}
                    className="h-9"
                    aria-label={monthName}
                    aria-current={month === index ? 'true' : undefined}
                  >
                    {monthName.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Year Selection */}
            <div>
              <label
                htmlFor="year-select"
                className="mb-2 block text-xs font-medium text-foreground"
              >
                Year
              </label>
              <Select
                id="year-select"
                options={yearOptions}
                value={year.toString()}
                onChange={(value) => value && handleYearChange(Number(value))}
                disabled={disabled}
                aria-label="Select year"
              />
            </div>
          </div>
        )}

        {/* Weekday headers */}
        <div className="mb-2 grid grid-cols-7 gap-1">
          {orderedWeekdays.map((day) => (
            <div
              key={day}
              className="flex h-8 items-center justify-center text-xs font-medium text-muted-foreground"
              aria-label={day}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days grid */}
        <table className="w-full border-separate border-spacing-1">
          <tbody>
            {Array.from({ length: 6 }).map((_, weekIndex) => {
              const weekStart = weekIndex * 7;
              const weekDays = calendarDays.slice(weekStart, weekStart + 7);
              const weekKey = `week-${weekIndex}`;

              return (
                <tr key={weekKey}>
                  {weekDays.map((calDay, dayIndex) => {
                    const cellKey = `${calDay.date.getFullYear()}-${calDay.date.getMonth()}-${calDay.date.getDate()}-${dayIndex}`;
                    const selected = isSelectedDay(calDay.date);
                    const inRange = isInRange(calDay.date);
                    const rangeStart = isRangeStartDay(calDay.date);
                    const rangeEnd = isRangeEndDay(calDay.date);
                    const dayMonth = calDay.date.toLocaleString('default', {
                      month: 'long',
                    });

                    return (
                      <td key={cellKey}>
                        <button
                          type="button"
                          onClick={() => handleDayClick(calDay.date)}
                          disabled={disabled}
                          className={cn(
                            getDayClasses(
                              selected,
                              inRange,
                              rangeStart,
                              rangeEnd
                            ),
                            !calDay.isCurrentMonth && 'text-muted-foreground/40'
                          )}
                          aria-label={`${dayMonth} ${calDay.day}, ${calDay.date.getFullYear()}`}
                          aria-current={selected ? 'date' : undefined}
                        >
                          {calDay.day}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
);

Calendar.displayName = 'Calendar';

export { Calendar };
export type { WeekStartDay };
export type { DateRange };
